import { Injectable } from '@angular/core';
import { MultistreammixerService } from './multistreammixer.service';
import { SocketioService } from './socketio.service';
import { ApiService } from './api.service';
import { UserSessionData } from '../meeting/sessiondata.model';

declare let MediaRecorder: any;

@Injectable({
  providedIn: 'root',
})
export class RecordingService {
  socket: any;
  mediaRecorder: any;
  audioRecorder: any;
  clientAudio: any;
  hostAudio: any;
  declare static videoRecordedBlobs: any;
  declare static audioRecordedBlobs: any;
  mixedStream: any;
  remoteStream: any;
  session_details: UserSessionData;

  constructor(private ss: SocketioService, private apiservice: ApiService) {
    this.socket = ss.socket;
    this.session_details = this.apiservice.session_details;
  }

  getSupportedMimeTypes(type?: 'video' | 'audio') {
    const videoTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/mp4;codecs=h264,aac',
    ];

    const audioTypes = [
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/webm;codecs=vorbis',
      'audio/ogg;codecs=vorbis',
      'audio/mp4;codecs=mp4a.40.2',
    ];

    let types = [];

    if (type === 'video') types = videoTypes;
    else if (type === 'audio') types = audioTypes;
    else types = [...videoTypes, ...audioTypes];

    return types.filter((mimeType) =>
      MediaRecorder.isTypeSupported(mimeType)
    );
  }

  checkSupportedMimeType(codecs: string) {
    let supportedTypes = this.getSupportedMimeTypes(
      codecs.startsWith('video') ? 'video' : 'audio'
    );
    return supportedTypes.includes(codecs);
  }

  startRecording(codecPreference: string, stream: any) {
    RecordingService.videoRecordedBlobs = [];
    RecordingService.audioRecordedBlobs = [];
    const type = this.session_details.calltype === 'video_call' ? 'video' : 'audio';

    //  check mime type
    const mimeSupport = this.getSupportedMimeTypes(type);
    console.log("mimeSupport:  >>>  ", mimeSupport, '\n', codecPreference)
    if (mimeSupport.includes(codecPreference)) {
      try {
        this.mediaRecorder = new MediaRecorder(stream, { mimeType: codecPreference });
        if (stream.getTracks().length > 1) {
          let audioTrack = new MediaStream();
          audioTrack.addTrack(stream.getAudioTracks()[0]);

          this.audioRecorder = new MediaRecorder(audioTrack, {
            mimeType: 'audio/webm;codecs=opus',
          });

          this.audioRecorder.start(1000);
        }
      } catch (e) {
        console.error('Exception while creating MediaRecorder:', e);
        return;
      }
    }
    this.mediaRecorder.onstart = (event: any) => {
      console.info('Video meeting is recording with end to end encryption!');
    };

    this.mediaRecorder.onpause = (event: any) => {
      // console.log('Recorder pause!');
    };

    this.mediaRecorder.onresume = (event: any) => {
      // console.log('Recorder resumed!');
    };

    this.mediaRecorder.onstop = (event: any) => {
      // console.log('Recorder stopped!');
    };

    this.mediaRecorder.start(1000);

    this.mediaRecorder.ondataavailable = this.handleDataAvailable.bind(this);

    if (this.audioRecorder)
      this.audioRecorder.ondataavailable = this.handleDataAvailable.bind(this);
  }

  async handleDataAvailable(event: any) {
    if (event.data &&
      event.data.type.startsWith('video') &&
      event.data.size > 0 &&
      this.session_details.calltype === 'video_call'
    ) {
      this.ss.socket.emit(
        'save-video-recording',
        this.session_details.session_id,
        { video: await event.data }
      );
    } else if (
      event.data &&
      event.data.type.startsWith('audio') &&
      event.data.size > 0 &&
      this.session_details.calltype === 'audio_call'
    ) {
      this.ss.socket.emit(
        'save-audio-recording',
        this.session_details.session_id,
        { audio: await event.data }
      );
    }
  }

  stopRecording() {
    if (this.audioRecorder) {
      this.audioRecorder.stop();
    }
    if (this.clientAudio) {
      this.clientAudio.stop();
    }
    if (this.hostAudio) {
      this.hostAudio.stop();
    }
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
  }

  recordAudio(stream1: MediaStream, stream2: MediaStream) {
    try {
      let clientAudioTrack = new MediaStream();
      clientAudioTrack.addTrack(stream1.getAudioTracks()[0]);
      this.clientAudio = new MediaRecorder(clientAudioTrack, {
        mimeType: 'audio/webm;codecs=opus',
      });

      let hostAudioTrack = new MediaStream();
      hostAudioTrack.addTrack(stream2.getAudioTracks()[0]);
      this.hostAudio = new MediaRecorder(hostAudioTrack, {
        mimeType: 'audio/webm;codecs=opus',
      });

      this.clientAudio.start(1000);
      this.hostAudio.start(1000);

      this.clientAudio.ondataavailable = async (event) => {
        if (
          event.data &&
          event.data.type == 'audio/webm;codecs=opus' &&
          event.data.size > 0 &&
          this.session_details.calltype === 'audio_call'
        ) {
          this.ss.socket.emit(
            'save-client-audio-recording',
            this.session_details.session_id,
            {
              clientAudio: await event.data,
            }
          );
        }
      };

      this.hostAudio.ondataavailable = async (event) => {
        if (
          event.data &&
          event.data.type == 'audio/webm;codecs=opus' &&
          event.data.size > 0 &&
          this.session_details.calltype === 'audio_call'
        ) {
          this.ss.socket.emit(
            'save-host-audio-recording',
            this.session_details.session_id,
            {
              hostAudio: await event.data,
            }
          );
        }
      };
    } catch (error) { }
  }

  handleSuccess(stream1: MediaStream, stream2: MediaStream, codecs: any) {
    this.remoteStream = stream1;
    let mixer = new MultistreammixerService([stream1, stream2]);
    mixer.frameInterval = 1;
    mixer.startDrawingFrames();
    let mixed = mixer.getMixedStream();
    this.mixedStream = mixer;
    this.startRecording(codecs, mixed);

    // Socket audio client and host event are commented out for now; enable in the future if needed.
    // this.recordAudio(stream1, stream2);
  }

  init(stream1: any, stream2: any, codecs: any) {
    try {
      this.handleSuccess(stream1, stream2, codecs);
    } catch (e) {
      console.error('error:', e);
    }
  }

  addStream(stream1: any, stream2: any) {
    try {
      if (this.mediaRecorder.mimeType === 'audio/webm;codecs=opus') {
        this.mediaRecorder.requestData();
        this.mediaRecorder.stop();
        let mixer = new MultistreammixerService([stream1, stream2]);
        mixer.frameInterval = 1;
        mixer.startDrawingFrames();
        let mixed = mixer.getMixedStream();
        this.startRecording('video/webm;codecs=vp9,opus', mixed);
      } else if (this.mediaRecorder.mimeType === 'video/webm;codecs=vp9,opus') {
        this.mediaRecorder.pause();
        let mixer = new MultistreammixerService([stream1, stream2]);
        mixer.frameInterval = 1;
        mixer.startDrawingFrames();
        let mixed = mixer.getMixedStream();
        this.mediaRecorder.stream.addTrack(mixed);
        this.mediaRecorder.resume();
      }
    } catch (error) { }
  }

  pauseAndResumeVideoStream(streams?: any) {
    try {
      if (!streams) {
        throw 'First parameter is required.';
      }
      if (!(streams instanceof Array)) {
        streams = [streams];
      }

      streams.forEach((stream) => {
        if (
          !stream.getVideoTracks().enabled && stream.getVideoTracks().state !== 'live'
        ) {
          return;
        } else {
          this.mixedStream.resetVideoStreams(streams);
        }
      });
    } catch (e) { }
  }
}
